import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { useUserCountry } from '@hooks/shared/useUserCountry';
import { RootState } from '@lib/redux.lib/store';
import type { JobPostStatusType } from '@lib/utils/constants/applicationConstants';

type Props = {
  children: ReactNode;
  initialJobsBoardSearchFilters: JobsBoardModalFilters;
};

export type JobsBoardModalFiltersWithToggle = {
  roleTypes?: string[];
  unionStatuses?: string[];
  applicationStatuses?: JobPostStatusType[];
};

export type JobsBoardModalFiltersWithSet = {
  genders?: string[];
  ethnicities?: string[];
  workDates?: string[];
  cityStateStringList?: string[];
};

export type JobsBoardModalFilters = JobsBoardModalFiltersWithToggle &
  JobsBoardModalFiltersWithSet & {
    ageRange?:
      | {
          min?: number;
          max?: number;
        }
      | undefined;
  };

type JobsBoardFiltersViewContextType = {
  jobsBoardSearchFilters: JobsBoardModalFilters;
  setJobsBoardSearchFilters: ReactStateUpdater<JobsBoardModalFilters>;
  publicViewCountry: ProductionCountry;
  setPublicViewCountry: ReactStateUpdater<ProductionCountry>;
  mainViewCountry: ProductionCountry;
  filtersCount: number;
  onClearFilters: () => void;
};

const JobsBoardFiltersViewContext = createContext(
  {} as JobsBoardFiltersViewContextType
);

export const JobsBoardFiltersViewContextProvider = ({
  children,
  initialJobsBoardSearchFilters,
}: Props): JSX.Element => {
  const [jobsBoardSearchFilters, setJobsBoardSearchFilters] =
    useState<JobsBoardModalFilters>(initialJobsBoardSearchFilters);

  const onClearFilters = useCallback(() => {
    setJobsBoardSearchFilters({});
  }, [setJobsBoardSearchFilters]);

  const [publicViewCountry, setPublicViewCountry] =
    useState<ProductionCountry>('US');

  const { userCountry } = useUserCountry();
  const loggedInUserId = useSelector(
    (state: RootState) => state.app?.user?.profile?.id
  );
  const isLoggedIn = useMemo(() => !!loggedInUserId, [loggedInUserId]);

  const filtersCount = useMemo(() => {
    return Object.keys(jobsBoardSearchFilters || {}).length;
  }, [jobsBoardSearchFilters]);

  const mainViewCountry = useMemo(() => {
    if (isLoggedIn) {
      return userCountry;
    }
    return publicViewCountry;
  }, [isLoggedIn, userCountry, publicViewCountry]);

  const prevMainViewCountryRef = useRef(mainViewCountry);

  useEffect(() => {
    if (prevMainViewCountryRef.current !== mainViewCountry) {
      prevMainViewCountryRef.current = mainViewCountry;
      setJobsBoardSearchFilters({}); // clear filters when main view country changes
    }
  }, [mainViewCountry, setJobsBoardSearchFilters]);

  return (
    <JobsBoardFiltersViewContext.Provider
      value={{
        jobsBoardSearchFilters,
        setJobsBoardSearchFilters,
        publicViewCountry,
        setPublicViewCountry,
        mainViewCountry,
        filtersCount,
        onClearFilters,
      }}
    >
      {children}
    </JobsBoardFiltersViewContext.Provider>
  );
};

export const useJobsBoardFiltersViewContext: () => JobsBoardFiltersViewContextType =
  () =>
    useContext<JobsBoardFiltersViewContextType>(JobsBoardFiltersViewContext);
